/**
 * Shared leaderboard row helpers used by:
 *   - LeaderboardScreen (full-screen, /api/daily-leaderboard/top)
 *   - LeaderboardCollapse (main-menu, top-3 + me preview)
 *
 * Pure helpers (`rankColor`, `formatScore`, `resolveDisplayName`) live alongside
 * the DOM-builder so the visual logic is exercised by unit tests without jsdom.
 */

export const TOP_MEDAL_COLORS: Readonly<Record<number, string>> = {
  1: '#FFD700',
  2: '#C0C0C0',
  3: '#CD7F32',
}

const FALLBACK_RANK_COLOR = '#aaa'

export function rankColor(rank: number): string {
  return TOP_MEDAL_COLORS[rank] ?? FALLBACK_RANK_COLOR
}

export function formatScore(score: number): string {
  if (!Number.isFinite(score)) return '0⭐'
  const clamped = Math.max(0, Math.floor(score))
  return `${clamped}⭐`
}

export function resolveDisplayName(name: string | null | undefined): string {
  if (typeof name !== 'string') return 'Anonymous'
  const trimmed = name.trim()
  return trimmed.length > 0 ? trimmed : 'Anonymous'
}

export interface RowEntry {
  rank: number
  displayName: string
  score: number
  games?: number
}

export interface BuildRowOptions {
  isMe?: boolean
  emptyState?: 'not_played' | null
  showGames?: boolean
}

/**
 * Construct a leaderboard row element. All user-provided text routed through
 * `textContent` — XSS-safe for arbitrary `displayName` values from the server.
 *
 * Variants:
 *   - default (`isMe=false`): subtle background, medal color for top-3
 *   - `isMe=true`: golden border, accented rank color
 *   - `emptyState='not_played'`: placeholder row "#— Не играл сегодня..."
 */
export function buildLeaderboardRow(
  entry: RowEntry,
  opts: BuildRowOptions = {}
): HTMLElement {
  const row = document.createElement('div')
  const variant = opts.emptyState === 'not_played'
    ? 'empty'
    : opts.isMe
      ? 'me'
      : 'row'
  row.setAttribute('data-row-variant', variant)

  if (variant === 'empty') {
    row.style.cssText = `
      display: flex; align-items: center; padding: 10px 12px;
      background: rgba(251,191,36,0.06); border: 1px dashed rgba(251,191,36,0.3);
      border-radius: 8px;
    `
    const rankEl = document.createElement('span')
    rankEl.style.cssText = 'width: 48px; text-align: center; font-weight: bold; color: #FBBF24;'
    rankEl.textContent = '#—'
    row.appendChild(rankEl)

    const msgEl = document.createElement('span')
    msgEl.style.cssText = 'flex: 1; margin-left: 8px; color: #FBBF24; font-size: 0.85rem;'
    msgEl.textContent = 'Не играл сегодня — сыграй первый раунд'
    row.appendChild(msgEl)
    return row
  }

  const isMe = variant === 'me'
  row.style.cssText = isMe
    ? `
      display: flex; align-items: center; padding: 10px 12px;
      background: rgba(251,191,36,0.12); border: 1px solid rgba(251,191,36,0.4);
      border-radius: 8px;
    `
    : `
      display: flex; align-items: center; padding: 8px 12px;
      background: rgba(255,255,255,0.04); border-radius: 8px;
    `

  const rankEl = document.createElement('span')
  const color = isMe ? '#FBBF24' : rankColor(entry.rank)
  rankEl.style.cssText = `width: ${isMe ? 48 : 32}px; text-align: center; font-weight: bold; color: ${color};`
  rankEl.textContent = isMe ? `#${entry.rank}` : String(entry.rank)
  row.appendChild(rankEl)

  const nameEl = document.createElement('span')
  nameEl.style.cssText = 'flex: 1; margin-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
  nameEl.textContent = resolveDisplayName(entry.displayName)
  row.appendChild(nameEl)

  const scoreEl = document.createElement('span')
  scoreEl.style.cssText = 'color: #FFD700; font-weight: bold; margin-right: 8px;'
  scoreEl.textContent = formatScore(entry.score)
  row.appendChild(scoreEl)

  if (opts.showGames && typeof entry.games === 'number') {
    const gamesEl = document.createElement('span')
    gamesEl.style.cssText = 'color: #94a3b8; font-size: 0.75rem; min-width: 56px; text-align: right;'
    const safeGames = Number.isFinite(entry.games) ? Math.max(0, Math.floor(entry.games)) : 0
    gamesEl.textContent = `${safeGames} games`
    row.appendChild(gamesEl)
  }

  return row
}
